import { useRef, useState } from 'react';
import { APP_SLUG, type FileKind, type FileRow } from 'shared';
import { api, fileUrl } from './api';
import { alert, confirm } from './telegram';
import Icon from './Icon';

/** Килобайты водителю ничего не говорят — показываем мегабайты. */
function size(bytes: number): string {
  return bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} КБ`
    : `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function isImage(mime: string): boolean {
  return mime.startsWith('image/');
}

/**
 * Имя для сохранения.
 *
 * Оригинальное, если оно есть: `f3a91c….pdf` в папке «Загрузки» через неделю
 * невозможно опознать. Расширение восстанавливаем по типу — часть файлов
 * приходит из камеры вообще без имени.
 */
export function downloadName(f: FileRow): string {
  if (f.originalName) return f.originalName;

  const ext = f.mimeType.includes('pdf')
    ? 'pdf'
    : (f.mimeType.split('/')[1] ?? 'bin').replace('jpeg', 'jpg');

  return `${APP_SLUG}-${f.id.slice(0, 8)}.${ext}`;
}

/**
 * Скачивание вложения.
 *
 * Не `<a download>`: ссылка подписанная и может отдаваться с другого хоста,
 * а там атрибут download браузером игнорируется — файл просто открывается
 * во вкладке. Поэтому качаем через fetch и сохраняем blob.
 */
export function DownloadButton({ file }: { file: FileRow }) {
  const [busy, setBusy] = useState(false);

  return (
    <button
      type="button"
      className="file-get"
      disabled={busy}
      aria-label={`Скачать ${downloadName(file)}`}
      data-tip="Скачать файл"
      onClick={async () => {
        setBusy(true);
        try {
          await api.download(fileUrl(file), downloadName(file));
        } catch {
          await alert('Не удалось скачать файл. Попробуйте ещё раз.');
        } finally {
          setBusy(false);
        }
      }}
    >
      <Icon name="download" size={14} />
    </button>
  );
}

interface Props {
  items: FileRow[];
  /** null — удалять нельзя (чужие документы). */
  onDelete?: (id: string) => void;
}

/** Плитка превью. PDF показываем значком: рисовать его в браузере дорого. */
export function FileGrid({ items, onDelete }: Props) {
  if (items.length === 0) return null;

  return (
    <div className="file-grid">
      {items.map((f) => (
        <div key={f.id} className="file-tile">
          <a href={fileUrl(f)} target="_blank" rel="noreferrer" title={f.originalName ?? ''}>
            {isImage(f.mimeType) ? (
              <img src={fileUrl(f)} alt={f.originalName ?? 'файл'} loading="lazy" />
            ) : (
              <span className="file-doc">
                <Icon name="doc" />
                PDF
              </span>
            )}
          </a>

          <div className="file-meta">
            <span>{size(f.sizeBytes)}</span>
            <DownloadButton file={f} />
            {onDelete ? (
              <button
                type="button"
                className="file-del"
                onClick={async () => {
                  if (!(await confirm('Удалить файл?'))) return;
                  await api.deleteFile(f.id).catch(() => {});
                  onDelete(f.id);
                }}
                aria-label="Удалить"
              >
                ×
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

interface UploadProps {
  kind: FileKind;
  orderId?: string;
  cargoId?: string;
  /** Документы техники: СТС, страховка, фото повреждений. */
  vehicleId?: string;
  docType?: string;
  label?: string;
  /** Показать вторую кнопку — снимок с камеры. Только на телефоне. */
  camera?: boolean;
  onDone: (row: FileRow) => void;
}

/**
 * Кнопка загрузки. Скрытый input, потому что стандартный
 * «Файл не выбран» в мобильном webview выглядит поломкой.
 */
export function UploadButton({
  kind,
  orderId,
  cargoId,
  vehicleId,
  docType,
  label,
  camera,
  onDone,
}: UploadProps) {
  const ref = useRef<HTMLInputElement>(null);
  const cam = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  // Камера предлагается только там, где она есть. В браузере на компьютере
  // атрибут capture открывает тот же выбор файла и путает.
  const hasCamera =
    camera && typeof navigator !== 'undefined' && /Android|iPhone|iPad/i.test(navigator.userAgent);

  async function pick(file: File) {
    setBusy(true);
    try {
      onDone(await api.upload(file, { kind, orderId, cargoId, vehicleId, docType }));
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  }

  return (
    <>
      <input
        ref={ref}
        type="file"
        hidden
        accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void pick(f);
        }}
      />
      {hasCamera && (
        <input
          ref={cam}
          type="file"
          hidden
          accept="image/*"
          capture="environment"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void pick(f);
          }}
        />
      )}

      {hasCamera && (
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() => cam.current?.click()}
        >
          <Icon name="camera" />
          Снять
        </button>
      )}

      <button
        type="button"
        className="secondary"
        disabled={busy}
        onClick={() => ref.current?.click()}
      >
        <Icon name="upload" />
        {busy ? 'Загружаю…' : (label ?? 'Прикрепить')}
      </button>
    </>
  );
}
